import { isAfter, isSameYear } from 'date-fns';
import { FilterableActivity } from './schemes';
import {
  Activity,
  ActivityTargetAge,
  ActivityTargetAgeOption,
  ActivityKeyword,
} from '~/schemes';
import { toDate, today as startOfToday } from '~/helpers';

/**
 * タイムゾーンオフセット文字列にマッチする正規表現
 */
const TMP_TIMEZONE_MATCH_RE = /(\+\d{2}:\d{2}|Z)$/;

/**
 * 日時っぽい文字列にJSTのオフセット文字列を強制でセットする
 * @param source - 日時っぽい文字列
 * @returns JSTのオフセット文字列が付与された日時文字列
 */
const convert2JST = (source: string): string => {
  source = source.replace(/\//g, '-');
  if (TMP_TIMEZONE_MATCH_RE.test(source)) {
    if (!source.includes('T')) source += 'T00:00:00';
    source += '+09:00';
  }
  return source;
};

/**
 * フィルタ処理用に開催期間が拡張されたアクティビティを取得する
 *
 * @param activity アクティビティ
 * @returns フィルタ処理用に開催期間が拡張されたアクティビティ
 *
 * @see {@link FilterableActivity}
 */
export function toFilterableActivity(activity: Activity): FilterableActivity {
  const computedActivity: FilterableActivity = {
    ...activity,
    computedPeriod: null,
  };
  const { period } = activity;
  if (period) {
    const { from, to } = period;
    computedActivity.computedPeriod = {
      from: (from && toDate(convert2JST(from))) || null,
      to: (to && toDate(convert2JST(to))) || null,
    };
  }
  return computedActivity;
}

/**
 * フィルタ処理用に開催期間が拡張されたアクティビティのリストを取得する
 *
 * @param activities アクティビティリスト
 * @returns フィルタ処理用に開催期間が拡張されたアクティビティのリスト
 *
 * @see {@link FilterableActivity}
 */
export function toFilterableActivities(
  activities: Activity[],
): FilterableActivity[] {
  return activities.map((activity) => toFilterableActivity(activity));
}

/**
 * 指定のアクティビティの開催日が、指定した月文字列、および当日において含まれているかチェックするフィルタ関数
 *
 * @param activity - 画面表示やフィルタ処理用に開催期間が拡張されたアクティビティ
 * @param month - 月文字列（1 - 12）
 * @param today - 当日を示すDateインスタンス
 * @returns 有効である場合true
 */
export function activityPeriodFilter(
  activity: FilterableActivity,
  month: string | null,
  today = startOfToday(),
) {
  const { computedPeriod } = activity;

  // 月を選択していないか、開催日設定がない場合は有効
  if (month === null || !computedPeriod) return true;

  const { from, to } = computedPeriod;
  if (!from && !to) return true;

  // 終了日の設定があって、当日がすでにすぎている場合は無効
  if (to && isAfter(today, to)) {
    return false;
  }

  // 開始日も終了日もどちらも設定がある場合
  if (from && to) {
    const selectedMonth = parseInt(month, 10) - 1;
    const fromMonth = from.getMonth();
    const toMonth = to.getMonth();
    if (isSameYear(from, to)) {
      if (selectedMonth < fromMonth || selectedMonth > toMonth) {
        return false;
      }
    } else {
      for (let m = fromMonth; m < 12; m++) {
        if (m === selectedMonth) return true;
      }
      for (let m = 0; m <= toMonth; m++) {
        if (m === selectedMonth) return true;
      }
      return false;
    }
  }
  return true;
}

/**
 * 指定のアクティビティが選択した対象年齢条件に含まれるかフィルタする関数
 * @param activity - アクティビティ
 * @param opt - 選択中の対象年齢オプション
 * @param includesAll - 対象年齢が「全て」の場合は強制採用するか（デフォルトはtrue）
 * @returns 有効である場合true
 */
export function activityTargetAgeFilter(
  activity: Activity,
  opt: ActivityTargetAgeOption | null,
  includesAll: boolean = true,
) {
  // 未選択(null)の場合は強制採用
  if (!opt) return true;
  const { value } = opt;
  if (value === null) return true;

  const { targetAge, targetAgeRange } = activity;

  // アクティビティーの対象年齢が「全て」の場合は強制採用
  if (includesAll && targetAge === ActivityTargetAge.All) return true;
  if (targetAgeRange && opt.range) {
    // アクティビティー＆選択値の両方に範囲指定のある場合は、交差していれば採用
    let { from: af = null, to: at = null } = targetAgeRange;
    const { from: bf, to: bt } = opt.range;

    if (af === null) af = 0;
    if (at === null) at = 999;

    /**
     * @see: https://qiita.com/kawasima/items/9e23544c95385658292a
     */
    return af <= bt && at >= bf;
  } else {
    return targetAge === opt.value;
  }
}

/**
 * 指定のアクティビティが指定した対象年齢条件リストの全てに合格するかフィルタする関数
 * @param activity - アクティビティ
 * @param options - 選択中の対象年齢オプションのリスト
 * @param includesAll - 対象年齢が「全て」の場合は強制採用するか（デフォルトはtrue）
 * @returns 有効である場合true
 */
export function activityTargetAgesFilter(
  activity: Activity,
  options: ActivityTargetAgeOption[],
  includesAll: boolean = true,
) {
  // 未選択の場合は強制採用
  if (!options.length) return true;

  return options.every((option) =>
    activityTargetAgeFilter(activity, option, includesAll),
  );
}

/**
 * 指定のアクティビティが指定したキーワードの値のリストのいずれかを含んでいるかフィルタする関数
 * @param activity - アクティビティ
 * @param values - キーワードの値のリスト
 * @returns いずれかのキーワードの値を含んでいる場合true
 */
export function activityKeywordsFilter(
  activity: Activity,
  values: ActivityKeyword['value'][],
) {
  // 未選択の場合は絞り込みしたくないので強制採用
  if (!values.length) return true;

  return values.some((value) => {
    return activity.keywords.includes(value);
  });
}
